/**
 * CalculatorResult — uniform result panel rendered for any calculator.
 *
 * Composes:
 *  • SeverityBadge + urgency pill + recommended specialist
 *  • CostMeter (when the calc emits a cost range)
 *  • NegotiationMeter
 *  • Cost breakdown table
 *  • Next steps + questions to ask
 *  • Contextual CTA block (default vs high-risk variant)
 *  • Copy / Email / PDF action row
 *  • EmailCaptureCard
 *
 * Everything is data-driven by the engine's CalculatorResult dict; this
 * component never branches on calculator slug.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Copy, Mail, FileDown, AlertTriangle, ClipboardCheck, ArrowRight, Share2,
} from "lucide-react";
import { toast } from "sonner";

import SeverityBadge from "@/components/calculators/SeverityBadge";
import CostMeter from "@/components/calculators/CostMeter";
import NegotiationMeter from "@/components/calculators/NegotiationMeter";
import EmailCaptureCard from "@/components/calculators/EmailCaptureCard";
import { downloadCalculatorPdf } from "@/lib/calculatorApi";
import { track } from "@/lib/analytics";

function buildTextSummary(calc, result) {
    const lines = [];
    lines.push(`${calc?.title || "Calculator result"} — Buyer's Leverage`);
    lines.push("");
    if (result?.cost_range_usd) {
        lines.push(
            `Estimated exposure: $${Math.round(result.cost_range_usd.low).toLocaleString()}–$${Math.round(result.cost_range_usd.high).toLocaleString()}`,
        );
    }
    if (result?.severity?.label) {
        lines.push(`Severity: ${result.severity.label}`);
    }
    if (result?.urgency?.label) {
        lines.push(`Urgency: ${result.urgency.label}`);
    }
    if (result?.negotiation?.label) {
        lines.push(`Negotiation relevance: ${result.negotiation.label}`);
    }
    if (result?.summary_text) {
        lines.push("");
        lines.push(result.summary_text);
    }
    return lines.join("\n");
}

export default function CalculatorResult({ calc, inputs, result }) {
    const textSummary = useMemo(
        () => buildTextSummary(calc, result),
        [calc, result],
    );

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(textSummary);
            toast.success("Summary copied to clipboard.");
        } catch (e) {
            toast.error("Copy failed — your browser may be blocking clipboard access.");
        }
    };

    const emailResult = () => {
        const subject = encodeURIComponent(
            `My ${calc?.title || "calculator"} result`,
        );
        const body = encodeURIComponent(
            textSummary + "\n\nView the full tool: https://buyersleverage.com/tools/" + calc?.slug,
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    const downloadPdf = async () => {
        try {
            await downloadCalculatorPdf(calc.slug, inputs);
            toast.success("PDF downloaded.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "PDF generation failed.");
        }
    };

    /**
     * Share via the device's native share sheet (iOS / Android / modern
     * desktop). Falls back to clipboard copy on browsers without the
     * Web Share API (older Safari, Firefox desktop).
     */
    const shareResult = async () => {
        const shareUrl =
            typeof window !== "undefined"
                ? `${window.location.origin}/tools/${calc?.slug}`
                : `https://buyersleverage.com/tools/${calc?.slug}`;
        const shareData = {
            title: `${calc?.title || "Calculator result"} — Buyer's Leverage`,
            text: textSummary,
            url: shareUrl,
        };
        // Web Share API path — iOS / Android / modern desktop
        if (typeof navigator !== "undefined" && navigator.share) {
            try {
                await navigator.share(shareData);
                track("calculator_result_shared", {
                    slug: calc?.slug, method: "native_share_sheet",
                });
                // Confirm to the user — the native share sheet's "Done"
                // dismissal otherwise leaves them with no in-app feedback.
                toast.success("Shared.");
                return;
            } catch (err) {
                // User canceled (AbortError) — silently ignore. Anything else
                // we fall through to clipboard.
                if (err?.name === "AbortError") return;
            }
        }
        // Clipboard fallback
        try {
            await navigator.clipboard.writeText(`${shareData.text}\n\n${shareUrl}`);
            toast.success(
                "Sharing isn't supported on this browser — link copied to clipboard instead.",
            );
            track("calculator_result_shared", {
                slug: calc?.slug, method: "clipboard_fallback",
            });
        } catch (e) {
            toast.error(
                "Couldn't open share sheet or copy to clipboard. Try the Copy summary button instead.",
            );
        }
    };

    if (!result) return null;

    const ctaDestination = result.cta?.href || "/upload";
    const isHighRisk = result.cta?.level === "high_risk";

    return (
        <div className="space-y-8" data-testid="calculator-result">
            {/* Headline pills */}
            <div className="flex flex-wrap items-center gap-2">
                <SeverityBadge severity={result.severity} size="lg" />
                {result.urgency?.label && (
                    <span
                        className="inline-flex items-center rounded-full bg-bl/8 px-3 py-1 text-xs font-semibold text-bl tracking-wide uppercase"
                        data-testid="urgency-badge"
                    >
                        {result.urgency.label}
                    </span>
                )}
                {result.recommended_specialist && (
                    <span className="text-xs text-bl-muted">
                        Recommended: {result.recommended_specialist}
                    </span>
                )}
            </div>

            {/* Cost meter */}
            {result.cost_range_usd && <CostMeter range={result.cost_range_usd} />}

            {/* Negotiation meter */}
            <NegotiationMeter negotiation={result.negotiation} />

            {/* Cost breakdown table */}
            {result.cost_breakdown?.length > 0 && (
                <div className="rounded-lg border border-bl/30 overflow-hidden">
                    <div className="bg-bl-bg px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        Cost breakdown
                    </div>
                    <div className="divide-y divide-bl/20">
                        {result.cost_breakdown.map((r, i) => (
                            <div
                                key={i}
                                className="px-4 py-3 flex items-baseline justify-between gap-3"
                                data-testid={`cost-row-${i}`}
                            >
                                <div className="flex-1">
                                    <div className="text-sm text-bl">{r.label}</div>
                                    {r.note && (
                                        <div className="text-[11px] text-bl-muted mt-0.5">{r.note}</div>
                                    )}
                                </div>
                                <div className="text-sm font-semibold text-bl tabular-nums">
                                    ${Math.round(r.low).toLocaleString()}
                                    <span className="text-bl-muted px-1">—</span>
                                    ${Math.round(r.high).toLocaleString()}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Summary text */}
            {result.summary_text && (
                <p className="text-sm text-bl leading-relaxed">{result.summary_text}</p>
            )}

            {/* Next steps */}
            {result.next_steps?.length > 0 && (
                <div className="space-y-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted flex items-center gap-2">
                        <ClipboardCheck className="h-3 w-3" />
                        Next steps
                    </div>
                    <ol className="space-y-2.5 text-sm text-bl">
                        {result.next_steps.map((step, i) => (
                            <li
                                key={i}
                                className="flex gap-3"
                                data-testid={`next-step-${i}`}
                            >
                                <span className="font-bold text-bl-teal">{i + 1}.</span>
                                <span>{step}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {/* Questions to ask */}
            {result.questions_to_ask?.length > 0 && (
                <div className="space-y-3 rounded-lg border border-bl/30 p-4 bg-bl-surface">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted">
                        Questions to ask the seller, inspector, or contractor
                    </div>
                    <ul className="space-y-1.5 text-sm text-bl">
                        {result.questions_to_ask.map((q, i) => (
                            <li
                                key={i}
                                className="flex gap-2"
                                data-testid={`question-${i}`}
                            >
                                <span className="text-bl-teal">•</span>
                                <span>{q}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Action row */}
            <div className="flex flex-wrap gap-2 pt-1">
                <button
                    type="button"
                    onClick={shareResult}
                    className="bl-btn-primary text-xs"
                    data-testid="share-result-btn"
                >
                    <Share2 className="h-3.5 w-3.5" />
                    Share results
                </button>
                <button
                    type="button"
                    onClick={copyToClipboard}
                    className="bl-btn-ghost text-xs"
                    data-testid="copy-result-btn"
                >
                    <Copy className="h-3.5 w-3.5" />
                    Copy summary
                </button>
                <button
                    type="button"
                    onClick={emailResult}
                    className="bl-btn-ghost text-xs"
                    data-testid="email-result-btn"
                >
                    <Mail className="h-3.5 w-3.5" />
                    Email to me
                </button>
                <button
                    type="button"
                    onClick={downloadPdf}
                    className="bl-btn-ghost text-xs"
                    data-testid="pdf-result-btn"
                >
                    <FileDown className="h-3.5 w-3.5" />
                    Download PDF
                </button>
            </div>

            {/* Contextual CTA */}
            {result.cta && (
                <div
                    className={`rounded-xl p-6 ${
                        isHighRisk
                            ? "bg-[var(--bl-dark)] text-white"
                            : "bg-bl-teal text-white"
                    }`}
                    data-testid={`calc-cta-${result.cta.level}`}
                >
                    {isHighRisk && (
                        <div className="flex items-center gap-2 text-[var(--bl-warn)] text-xs font-semibold tracking-wide uppercase mb-2">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Higher-risk result
                        </div>
                    )}
                    <h3 className="text-lg font-bold">{result.cta.headline}</h3>
                    <p className="text-sm text-white/85 mt-2">{result.cta.subtext}</p>
                    <Link
                        to={ctaDestination}
                        className="inline-flex items-center gap-2 mt-4 rounded-md bg-white px-4 py-2 text-sm font-semibold text-[var(--bl-dark)] hover:bg-white/90"
                        data-testid="calc-cta-btn"
                    >
                        {result.cta.button}
                        <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            )}

            {/* Email capture */}
            <EmailCaptureCard slug={calc.slug} inputs={inputs} result={result} />
        </div>
    );
}
